import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { clearLoginAttemptInfo, consumeCode } from "supertokens-web-js/recipe/passwordless";
import Session from "supertokens-web-js/recipe/session";

import { AuthenticationPage } from "@/components/AuthenticationPage/AuthenticationPage";
import { Button } from "@/design-system/Button";
import { CodeInput, type CodeInputHandle } from "@/design-system/CodeInput";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useInterval } from "@/hooks/useInterval";
import { useIsWindowFocused } from "@/hooks/useIsWindowFocused";
import { useQueryParam } from "@/hooks/useQueryParam";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const SESSION_POLL_INTERVAL_MS = 30_000;

export function MagicLinkEnterCodePage(): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authCodeRef = useRef<CodeInputHandle>(null);
  const [email] = useQueryParam("email");

  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const navigateToRegistration = useCallback(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const registrationCode = queryParams.get("code");
    const type = queryParams.get("type");

    const newQueryParams = new URLSearchParams();
    if (registrationCode) {
      newQueryParams.set("code", registrationCode);
    }

    if (type) {
      newQueryParams.set("type", type);
    }

    void navigate(`${routes.registration.main()}?${newQueryParams.toString()}`);
  }, [navigate]);

  const onSubmit = useCallback(
    async (inputCode: string) => {
      if (inputCode.length !== 6 || isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      setError(undefined);

      try {
        const response = await consumeCode({ userInputCode: inputCode });

        if (response.status === "OK") {
          if (response.createdNewRecipeUser) {
            navigateToRegistration();
          } else {
            // Existing user just logging in - same as a regular login, skip straight to the app
            void navigate("/");
          }
        } else if (response.status === "INCORRECT_USER_INPUT_CODE_ERROR") {
          setError(t("page.magic-link.enter-code.error.invalid"));
          setCode("");
          authCodeRef.current?.clear();
        } else if (response.status === "EXPIRED_USER_INPUT_CODE_ERROR") {
          setError(t("page.magic-link.enter-code.error.expired"));
        } else {
          // RESTART_FLOW_ERROR — too many failed attempts
          await clearLoginAttemptInfo();
          void navigate(routes.authentication.magicLinkSent());
        }
      } catch (err: any) {
        if (err.isSuperTokensGeneralError === true) {
          setError(err.message);
        } else {
          setError(t("common.generic-error"));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, navigate, navigateToRegistration, t],
  );

  const isWindowFocused = useIsWindowFocused();

  useInterval(async () => {
    if (isWindowFocused && (await Session.doesSessionExist())) {
      navigateToRegistration();
    }
  }, SESSION_POLL_INTERVAL_MS);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      if (await Session.doesSessionExist()) {
        if (!cancelled) {
          navigateToRegistration();
        }
      }
    }

    window.addEventListener("focus", checkSession);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", checkSession);
    };
  }, [navigateToRegistration]);

  const handleCodeChange = useCallback(
    (newCode: string) => {
      setCode(newCode);
      if (newCode.length === 6) {
        void onSubmit(newCode);
      }
    },
    [onSubmit],
  );

  return (
    <AuthenticationPage
      header={t("page.magic-link.enter-code.title")}
      body={t("page.magic-link.enter-code.description")}
    >
      <Stack gap="lg">
        <CodeInput
          ref={authCodeRef}
          aria-label={t("page.magic-link.enter-code.title")}
          onChange={handleCodeChange}
          disabled={isSubmitting}
        />
        {error && <p className="text-caption text-text-danger-secondary">{error}</p>}
        <Button fullWidth onClick={() => void onSubmit(code)} isLoading={isSubmitting}>
          {t("page.magic-link.enter-code.submit")}
        </Button>
        <Inline justify="center" wrap={false}>
          <Button
            variant="ghostPrimary"
            type="link"
            href={`${routes.authentication.magicLinkSent()}?email=${encodeURIComponent(email || "")}`}
          >
            {t("page.magic-link.enter-code.back")}
          </Button>
        </Inline>
      </Stack>
    </AuthenticationPage>
  );
}
